import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queryKeys } from '@/shared/constants/query-keys'
import { agenciesService } from '../services/agencies.service'

/**
 * Lookup mínimo ({ _id, id, name, default, company }) para selects
 * dependientes (clientes, negociaciones). `AGENCIES_DEFAULT` expone las
 * predeterminadas (una por empresa) para preseleccionar. El listado admin
 * vive en use-agencies-admin-query.ts.
 */
export type IAgencyLookup = {
  _id: string
  id: string
  /**
   * El `agc_` con el que v2 direcciona esta agencia. Viaja al lado del `_id` para que
   * un select pueda quedarse en este lookup y aun así resolver lo elegido a una
   * referencia v2, cruzando por clave primaria del mismo documento. Ver ADR 0027.
   *
   * Opcional porque el schema lo declara así: un registro anterior al backfill no lo
   * tiene, y quien traduce debe FALLAR en vez de mandar `undefined`.
   */
  publicId?: string | null
  name: string
  default: boolean
  company: string
}

type UseAgenciesLookupQuery = {
  data: IAgencyLookup[] | undefined
  status: UseQueryResult['status']
  isLoading: boolean
  AGENCIES_DEFAULT: IAgencyLookup[] | undefined
}

export const useAgenciesLookupQuery = (dependence = true): UseAgenciesLookupQuery => {
  const { data, status, isLoading } = useQuery<IAgencyLookup[]>({
    queryKey: queryKeys.agencies.lookup(),
    queryFn: async () => {
      try {
        return await agenciesService.getLookup()
      } catch (e) {
        console.error(e)
        return []
      }
    },
    refetchOnWindowFocus: false,
    enabled: dependence
  })

  const AGENCIES_DEFAULT = useMemo<IAgencyLookup[]>(
    () => data?.filter((a) => a.default) ?? [],
    [data]
  )

  return { data, status, isLoading, AGENCIES_DEFAULT }
}
